'use client';

import { useParams } from 'next/navigation';
import { useRef, useState } from 'react';
import useContent from '@/app/(home)/services/useContent';
import useInfiniteLoading from '@/helpers/useInfiniteLoading';
import FlexComponent from '@/components/FlexComponent';
import MyLoader from '@/components/MyLoader';
import Header from '@/components/Header';
import SourcePageTrackerProvider from '@/context/sourcePageTracker/sourcePageTrackerProvider';
import usePageScrollHold from '@/helpers/usePageScrollHold';
import useLayoutConfig from '@/context/layoutConfig/useLayoutConfig';
import useRecommendations from '@/app/(home)/services/useRecommendations';
import useAuth from '@/context/auth/useAuth';

interface ContentPageProps {
    isBabiesMode: boolean;
}

const ChildComponent = ({ isOpen, setIsOpen }) => {
    return <div>{isOpen}</div>;
};

export default function ContentPage({ isBabiesMode = false }: ContentPageProps) {
    const [isOpen, setIsOpen] = useState(false);
    const { id } = useParams();
    const pageContainerRef = useRef(null);
    useLayoutConfig({ showNavbar: true, showHobe: true });
    const { userIsLoggedIn } = useAuth();
    const {
        data,
        error,
        isFetchingNextPage,
        fetchNextPage,
        videoResumeListCardsData,
        audioResumeListCardsData
    } = useContent({ isBabiesMode, pageId: 'AbzSNnMA' });
    const hasRecommendation = data?.pages
        ?.flatMap((page) => page?.results?.sections || [])
        .some(({ contents_generator }) => contents_generator === 'Recommendation');
    const { recommendationsData } = useRecommendations({
        fetchUserRecommendations: !!hasRecommendation && userIsLoggedIn
    });

    usePageScrollHold({
        key: 'contentProviderPages',
        id: id || (isBabiesMode ? 'homePageForBabies' : 'homePage'),
        pageContainerRef
    });

    useInfiniteLoading({
        scrollingElementSelector: '#content-page-container',
        loaderMethod: () => setTimeout(fetchNextPage, 0),
        toBeWatchedData: data,
        loading: isFetchingNextPage
    });

    const getCardsFromOtherSources = (contentGenerator, contentType) => {
        if (contentGenerator === 'Stream Log') {
            if (contentType === 'Video') return videoResumeListCardsData?.pages?.[0]?.data;
            else return audioResumeListCardsData?.pages?.[0]?.data;
        } else if (contentGenerator === 'Recommendation')
            return recommendationsData?.pages?.[0]?.data;

        return null;
    };

    return (
        <SourcePageTrackerProvider sourcePageData={{ sourcePage: isBabiesMode ? 'Child' : 'Kid' }}>
            <Header />
            <ChildComponent isOpen={isOpen} setIsOpen={setIsOpen} />
            <main
                className="content-page-container page-container pb-24 pt-4"
                id="content-page-container"
                ref={pageContainerRef}>
                {data?.pages?.map((page) =>
                    page?.results?.sections
                        ?.filter(
                            (section) => !['Story', 'Character'].includes(section.content_type)
                        )
                        .map((data, index) => (
                            <FlexComponent
                                key={index}
                                content={{
                                    ...data,
                                    cards:
                                        getCardsFromOtherSources(
                                            data?.contents_generator,
                                            data?.content_type
                                        ) ?? data.cards
                                }}
                            />
                        ))
                )}
                {isFetchingNextPage && (
                    <div className="timeline-loading">
                        <MyLoader width={32} />
                    </div>
                )}
            </main>
        </SourcePageTrackerProvider>
    );
}
